import { useCallback, useId, useState, type FC, type FormEvent } from "react";
import {
    Dialog,
    DialogHeader,
    DialogContent,
    DialogFooter,
    DialogClose,
    DialogBody,
    DialogTitle,
    DialogDescription,
} from "@/components/podkit/dialog/Dialog";
import { Text } from "@/components/podkit/typography/Text";
import { Label } from "@/components/podkit/forms/Label";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Input } from "@/components/podkit/forms/Input";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/podkit/select/Select";
import { formatError } from "@/utils/errors";
import { Button } from "@/components/flexkit/Button";
import { CopyableTextArea } from "@/components/podkit/forms/CopyableTextArea";
import { useCreateServiceAccountToken, type PlainServiceAccountToken } from "@/queries/service-account-token-queries";
import type { ServiceAccountHeaders } from "@/api";
import { TrackLocations } from "@/hooks/use-segment";

type Props = {
    open: boolean;
    onClose: () => void;
    serviceAccountId: string;
    headers: ServiceAccountHeaders;
};

export const ServiceAccountNewTokenModal: FC<Props> = ({ open, onClose, serviceAccountId, headers }) => {
    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );
    const [token, setToken] = useState<string>();
    const [sat, setSat] = useState<PlainServiceAccountToken>();

    const handleClose = useCallback(() => {
        setToken(undefined);
        setSat(undefined);
        onClose();
    }, [onClose]);

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent data-testid="service-account-new-token-modal" data-track-location={TrackLocations.ServiceAccountNewTokenModal}>
                <DialogHeader>
                    <DialogTitle>New service account token</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                {!token && (
                    <ServiceAccountNewTokenForm
                        serviceAccountId={serviceAccountId}
                        headers={headers}
                        onSuccess={(sat, tkn) => {
                            setSat(sat);
                            setToken(tkn);
                        }}
                    />
                )}
                {token && sat && <ServiceAccountNewTokenResult sat={sat} token={token} onClose={handleClose} />}
            </DialogContent>
        </Dialog>
    );
};

type ServiceAccountNewTokenFormProps = {
    serviceAccountId: string;
    headers: ServiceAccountHeaders;
    onSuccess?: (sat: PlainServiceAccountToken, token: string) => void;
};

const ServiceAccountNewTokenForm: FC<ServiceAccountNewTokenFormProps> = ({ serviceAccountId, headers, onSuccess }) => {
    const { toast } = useToast();

    const [description, setDescription] = useState<string>();
    const [validForDays, setValidForDays] = useState<string>("30 days");
    const inputDescriptionID = useId();
    const inputValidForID = useId();
    const createSAT = useCreateServiceAccountToken({ headers });

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLElement>) => {
            event.preventDefault();
            if (!description || !validForDays) {
                return;
            }

            try {
                const validForDaysNumber = parseInt(validForDays.substring(0, validForDays.length - " days".length));
                const { token, serviceAccountToken } = await createSAT.mutateAsync({
                    serviceAccountId,
                    description,
                    validForDays: validForDaysNumber,
                });

                onSuccess?.(serviceAccountToken, token);
            } catch (error) {
                toast({
                    title: "Failed to create service account token.",
                    description: formatError(error),
                });
            }
        },
        [description, validForDays, serviceAccountId, onSuccess, toast, createSAT],
    );

    return (
        <>
            <DialogBody>
                <form onSubmit={handleSubmit} data-testid="new-service-account-token-form">
                    <div className="flex flex-col gap-7">
                        <div className="flex flex-col gap-2">
                            <div className="space-y-1">
                                <Label htmlFor={inputDescriptionID}>Description</Label>
                                <Input
                                    id={inputDescriptionID}
                                    type="text"
                                    name="name"
                                    value={description || ""}
                                    placeholder="Enter a description"
                                    onChange={(e) => setDescription(e.target.value)}
                                    data-testid="service-account-token-description-input"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor={inputValidForID}>Token should be valid for</Label>
                                <Select
                                    name="valid_for"
                                    value={validForDays || ""}
                                    onValueChange={(e) => setValidForDays(e)}
                                >
                                    <SelectTrigger id={inputValidForID} loading={false}>
                                        <SelectValue placeholder={"30 days"}>{validForDays}</SelectValue>
                                        <SelectContent>
                                            <SelectGroup>
                                                <SelectItem key={30} value={"30 days"}>
                                                    <div className="flex w-full flex-row items-center justify-between pr-10">
                                                        <div>30 days</div>
                                                    </div>
                                                </SelectItem>
                                                <SelectItem key={60} value={"60 days"}>
                                                    <div className="flex w-full flex-row items-center justify-between pr-10">
                                                        <div>60 days</div>
                                                    </div>
                                                </SelectItem>
                                                <SelectItem key={90} value={"90 days"}>
                                                    <div className="flex w-full flex-row items-center justify-between pr-10">
                                                        <div>90 days</div>
                                                    </div>
                                                </SelectItem>
                                            </SelectGroup>
                                        </SelectContent>
                                    </SelectTrigger>
                                </Select>
                            </div>
                        </div>
                    </div>
                </form>
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" size="md">
                        Cancel
                    </Button>
                </DialogClose>
                <Button
                    aria-label="create"
                    autoFocus={true}
                    data-testid="create-service-account-token-button"
                    disabled={!description || !validForDays}
                    form="new-service-account-token-form"
                    loading={createSAT.isPending}
                    onClick={(e) => handleSubmit(e)}
                    type="submit"
                    size="md"
                    variant="primary"
                    data-tracking-id="create-sat-new-service-account-token-modal"
                >
                    Create
                </Button>
            </DialogFooter>
        </>
    );
};

type ServiceAccountNewTokenResultProps = {
    sat: PlainServiceAccountToken;
    token: string;
    onClose: () => void;
};

const ServiceAccountNewTokenResult: FC<ServiceAccountNewTokenResultProps> = ({ sat, token, onClose }) => {
    return (
        <>
            <DialogBody>
                <div className="flex flex-col gap-4">
                    <Text className="text-lg">
                        Be sure to copy your service account token now, as it won&apos;t be viewable again.
                    </Text>
                    <div className="flex flex-col gap-2 rounded-lg border border-border-base bg-surface-secondary p-4">
                        <div className="flex flex-col gap-1">
                            <Text className="text-sm text-content-secondary">Description</Text>
                            <Text className="text-sm font-medium">{sat.description}</Text>
                        </div>
                        <div className="flex flex-col gap-1">
                            <Text className="text-sm text-content-secondary">Expires</Text>
                            <Text className="text-sm font-medium">
                                {sat.expiresAt
                                    ? new Date(Number(sat.expiresAt.seconds) * 1000).toLocaleDateString()
                                    : "Never"}
                            </Text>
                        </div>
                        <CopyableTextArea
                            copyable
                            className="h-32 text-base text-content-secondary"
                            value={token}
                            data-testid="service-account-token-value"
                            data-tracking-id="copy-sat-new-service-account-token-modal"
                        />
                    </div>
                </div>
            </DialogBody>
            <DialogFooter>
                <Button size="md" variant="primary" onClick={onClose} data-tracking-id="done-service-account-token-modal">
                    Done
                </Button>
            </DialogFooter>
        </>
    );
};
